import { Tabs, BreadCrumb, Notification } from '@weirgroup/weir-dls';
import { groupReplacementParts } from '../../utils/replacement-parts.util';
import './styles.scss';
import { AssetOverview,DocumentRepository,Geofence,LocationTab, TransferTab } from './tabs';
import { ReplacementParts } from '../../components/Modal/components/replacement-parts-tab';
import { AddBillOfMaterial } from '../../components/Modal/components/add-bill-of-material';
import { useNavigate } from "react-router-dom";
import { AssetOverviewTabProps, AssetType } from './asset-detail-types';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { routePath } from '../../constants/navigation.constants';
import { BucketDataType, TagType, AssetType as Asset } from '../../types/bucketTypes';
import { useDarkMode } from '../../hooks/use-darkmode';
import { getImgSrc } from '../../helpers/getImageSrc';
import { useGetAssetAPI } from '../../hooks/use-asset-api';

const loadStateFromLocalStorage = (key: string): BucketDataType | undefined => {
 try {
        const serializedState = localStorage.getItem(key);
        if (serializedState === null) {
            return undefined;
            }
        return JSON.parse(serializedState);
    } catch (err) {
        console.error("Could not load state", err);
        return undefined;
    }
};
const NotificationContainer: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    return (
        <div className="notification-container-outer">
            <div className="notification-container-inner">{children}</div>
        </div>
    );
}

interface BreadCrumbContainerProps {
    selectedAsset: BucketDataType | undefined;
    asset?: Asset;
    t: (key: string) => string;
    navigate: (path: string) => void;
}
const BreadCrumbContainer: React.FC<BreadCrumbContainerProps> = ({ selectedAsset, asset, t, navigate }) => {
    return (
        <div className='breadcrumb-container'>
            <BreadCrumb
                menuItems={[
                    t('homepage'),
                    `${selectedAsset?.tag_id ?? asset?.tag?.esn ?? ""}`
                ]}
                onItemClick={(e: string) => {
                    if (e === "Homepage") {
                        navigate(routePath.HOMEPAGE)
                    }
                }}
                outerContainerPadding="30px 30px 180px 70px"
            />
        </div>
    );
}

export default function AssetDetailPage() {
    // State for assetStatus, always in sync with selectedAsset and network
    // State for active tab and Bill of Material accordion
    const [activeTabIndex, setActiveTabIndex] = React.useState<number>(0); // 0 = Asset Overview tab
    const [showAddBillOfMaterial, setShowAddBillOfMaterial] = React.useState<boolean>(false);
    const [editBill, setEditBill] = React.useState<any>(undefined);
    // Handlers for modal logic
    const toggleBillOfMaterial = (isEdit?: boolean, partData?: any) => {
        if (isEdit) {
            setEditBill(partData);
            setShowAddBillOfMaterial(true);
        } else {
            setShowAddBillOfMaterial(true);
        }
    };
    // Handler for AddBillOfMaterial cancel
    const handleBillModalCancel = () => {
        setShowAddBillOfMaterial(false);
        setActiveTabIndex(1); // Replacement Parts tab
    };
    const {darkMode} = useDarkMode();
    const navigate = useNavigate();
    const { t } = useTranslation();



    // selectedAsset is now local state, initialized from localStorage
    const [selectedAsset, setSelectedAsset] = React.useState<BucketDataType | undefined>(() => loadStateFromLocalStorage('selectedAsset'));
    const [tagInfo, setTagInfo] = React.useState<TagType | undefined>(() => loadStateFromLocalStorage('tagInfo') as TagType | undefined);
    const [assetStatus, setAssetStatus] = React.useState<string>(selectedAsset?.asset_status ?? "");
    React.useEffect(() => {
        setSelectedAsset(loadStateFromLocalStorage('selectedAsset'));
        setTagInfo(loadStateFromLocalStorage('tagInfo') as TagType | undefined);
        setAssetStatus(loadStateFromLocalStorage('selectedAsset')?.asset_status ?? "");
    }, []);
    const getAssetAPIHook = useGetAssetAPI(selectedAsset?.asset_id);

    // Sync selectedAsset and localStorage when API response changes
    React.useEffect(() => {
        if (getAssetAPIHook?.data) {
            let data = { ...getAssetAPIHook.data.bucket };
            data.asset_status = getAssetAPIHook.data.asset_status ?? "";
            // Ensure equipment_model is always up-to-date
            data.model = getAssetAPIHook.data.bucket?.model ?? "";
            setSelectedAsset(data);
            localStorage.setItem('selectedAsset', JSON.stringify(data));
            setAssetStatus(data.asset_status);
        }
    }, [getAssetAPIHook?.data?.asset_status]);

    const [notificationData, setNotificationData] = React.useState({
        title: t('notification.successTitle'),
        message: '',
        type: '',
        open:false
    });

    // Local state for replacement parts, initialized from selectedAsset
    const [replacementParts, setReplacementParts] = React.useState<any[]>(selectedAsset?.replacement_parts ?? []);

    // Sync replacementParts state when selectedAsset changes (e.g. after save or refresh)
    React.useEffect(() => {
        setReplacementParts(selectedAsset?.replacement_parts ?? []);
        setAssetStatus(selectedAsset?.asset_status ?? "");
    }, [selectedAsset]);

    const tabList: AssetOverviewTabProps[] = [
        { component: <AssetOverview 
            assetStatus={assetStatus}
            asset={selectedAsset}
            tagData={tagInfo}
        />, title: t('tabs.assetOverview') },
        { component: <ReplacementParts
            selectedAsset={selectedAsset as BucketDataType}
            setShowBillOfMaterial={setShowAddBillOfMaterial}
            groupedReplacementParts={groupReplacementParts(replacementParts)}
            toggleBillOfMaterial={toggleBillOfMaterial}
            setEditBill={setEditBill}
            isGuest={false}
        />, title: t('tabs.replacementParts') },
        { component: <DocumentRepository asset={selectedAsset as unknown as AssetType} setNotification={setNotificationData}/>, title: t('tabs.documents') },
        { component: <Geofence tagData={tagInfo as unknown as TagType} />, title: t('tabs.geofences') },
        { component: <TransferTab asset={selectedAsset as unknown as BucketDataType} />, title: t('tabs.transfer') },
        { component: <LocationTab asset={selectedAsset as unknown as BucketDataType} tagData={tagInfo as unknown as TagType}/>, title: t('tabs.location') }
    ];
    React.useEffect(() => {
    if (notificationData.open) {
        const timer = setTimeout(() => {
        setNotificationData((prev) => ({ ...prev, open: false }));
        }, 3000);
        return () => clearTimeout(timer);
    }
    }, [notificationData.open]);
    return (
        <div className='assetDetailPage'>
            {notificationData.open &&
            <NotificationContainer>
                <Notification
                callToActionList={[]}
                darkMode
                onClose={()=>setNotificationData({ ...notificationData, open: false })}
                textContent={notificationData.message}
                title={notificationData.title}
                variant={notificationData.type}
                />
            </NotificationContainer>
            }
            <BreadCrumbContainer selectedAsset={selectedAsset} asset={getAssetAPIHook?.data} t={t} navigate={navigate} />
            <div className='contentContainer'>
                <div className="modal-title-style">
                <p className="modal-title" style={{color:darkMode ? "white" : "black"}}><img style={{color:darkMode ? "white" : "black"}} src={getImgSrc(tagInfo as TagType, false)} alt="AssetIcon" /></p>
                <p className="modal-title" style={{color:darkMode ? "white" : "black"}}>
                  {selectedAsset?.serial_number}
                </p>
                </div>
                {showAddBillOfMaterial ? (
                    <AddBillOfMaterial
                        setShowAddNonEscoPart={setShowAddBillOfMaterial}
                        setActiveTabIndex={setActiveTabIndex}
                        selectedAsset={selectedAsset ?? {} as BucketDataType}
                        setSelectedAsset={setSelectedAsset}
                        editPart={editBill}
                        fetchBucketList={async () => {}}
                        onCancel={handleBillModalCancel}
                    />
                ) : (
                    <Tabs
                        items={tabList}
                        activeTabIndex={activeTabIndex}
                        onTabChange={setActiveTabIndex}
                    />
                )}
            </div>
        </div>
    );
}